import type { ChangeEvent } from 'react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import SearchRounded from '@mui/icons-material/SearchRounded';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import ModalCloseHeader from 'src/components/common/Modal/ModalCloseHeader';
import TranslationToggle from 'src/components/common/TranslationToggle';
import { useDashboardContext } from 'src/components/dashboard/DashboardContext';
import { useDashboardCommentContext } from 'src/components/dashboard/DashboardContext/CommentsContext';
import type {
  SentimentFilter,
  TopicFilter,
} from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/types';

import CommentsListDownload from './CommentsListDownload';
import CommentsListItem from './CommentsListItem';
import HRQSCommentsListDownload from './HRQSCommentsListDownload';

import { styles } from './index.styles';

export const COMMENTS_LIST_MODAL_TEST_ID = 'comments-list-modal';
export const COMMENTS_LIST_MODAL_TEXT_FILTER_TEST_ID =
  'comments-list-modal-text-filter';

export interface CommentsListModalProps {
  showTranslationToggle?: boolean;
  question: string | null;
  filterData?: {
    sentimentFilter?: SentimentFilter;
    topicFilter?: TopicFilter;
  };
  topics: Array<string>;
  isOpen: boolean;
  onClose: (x?: boolean) => void;
  initialTextFilter?: string;
}

const CommentsListModal = ({
  showTranslationToggle = true,
  question,
  filterData = {},
  isOpen,
  onClose,
  topics,
  initialTextFilter = '',
}: CommentsListModalProps) => {
  const { t } = useTranslation('dashboard');
  const {
    comments,
    currentPage,
    loading,
    textFilterLoading,
    textFilter,
    totalPageCount,
    setQuestion,
    setCommentIds,
    setCurrentPage,
    updateTextFilter,
    source,
  } = useDashboardCommentContext();
  const { dashboardConfig } = useDashboardContext();

  useEffect(() => {
    if (isOpen) {
      setQuestion(question);
      setCommentIds(topics);
      setCurrentPage(1);
      updateTextFilter(initialTextFilter);
    }
  }, [isOpen]);

  const [isDefaultLanguage, setIsDefaultLanguage] = useState<boolean>(true);
  const handleToggleIsDefaultLanguage = (
    _event: React.ChangeEvent<HTMLInputElement>,
  ) => setIsDefaultLanguage((isDefaultLanguage) => !isDefaultLanguage);

  const handleTextFilterInput = useCallback((event: ChangeEvent<any>) => {
    updateTextFilter(event.target.value);
  }, []);

  const onBackdropClick = (event: object, reason: string) => {
    if (isOpen && (reason === 'backdropClick' || reason === 'escapeKeyDown'))
      onClose(true);
  };

  const renderDownloadButton = () => {
    switch (source) {
      case 'hrqs':
        return (
          <HRQSCommentsListDownload
            topics={topics}
            useTranslatedText={!isDefaultLanguage}
          />
        );
      case 'topics':
        return (
          <CommentsListDownload
            question={question}
            filterData={filterData}
            topics={topics}
            isDefaultLanguage={!isDefaultLanguage}
          />
        );
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onBackdropClick}
      PaperProps={{ sx: styles.paper }}
      data-test-id={COMMENTS_LIST_MODAL_TEST_ID}
    >
      <DialogTitle component="div">
        <Stack
          direction="row"
          sx={{
            justifyContent: showTranslationToggle ? 'space-between' : 'end',
          }}
        >
          {showTranslationToggle && (
            <TranslationToggle
              isDefaultLanguage={isDefaultLanguage}
              onToggle={handleToggleIsDefaultLanguage}
            />
          )}
          <ModalCloseHeader onClose={() => onClose(false)} />
        </Stack>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <Typography variant={'h3'}>
            {t(`OpenCommentsList.Comments`)}
          </Typography>
          {dashboardConfig?.allow_open_comments_download &&
            !loading &&
            !!comments.length && (
              <DialogActions sx={styles.commentsDownload}>
                {renderDownloadButton()}
              </DialogActions>
            )}
        </Stack>
      </DialogTitle>
      <DialogContent sx={styles.modalContent}>
        <Box sx={styles.scrollBox}>
          <InfiniteScroll
            next={() => {
              setCurrentPage((prevState) => prevState + 1);
            }}
            hasMore={currentPage < totalPageCount || loading}
            style={styles.infiniteScroll}
            loader={
              <Box sx={styles.spinnerBox}>
                <CircularProgress />
              </Box>
            }
            dataLength={comments.length}
            height="100%" // Important: This setting is required in order to have infinity scroll working
          >
            <Box sx={styles.commentsSearch}>
              <TextField
                id="search-input"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ maxLength: 80 }}
                placeholder={t('OpenCommentsListFilter.searchComments')}
                onChange={handleTextFilterInput}
                size="medium"
                fullWidth
                value={textFilter}
                data-test-id={COMMENTS_LIST_MODAL_TEXT_FILTER_TEST_ID}
              />
            </Box>
            {textFilterLoading || (loading && !comments.length) ? (
              <Box sx={styles.spinnerBox}>
                <CircularProgress />
              </Box>
            ) : comments.length ? (
              comments.map((comment, index) => (
                <CommentsListItem
                  comment={comment}
                  key={index}
                  isDefaultLanguage={isDefaultLanguage}
                />
              ))
            ) : (
              <Typography sx={styles.noComments}>
                {t(`OpenCommentsList.noComments`)}
              </Typography>
            )}
          </InfiniteScroll>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CommentsListModal;
